import { type ItemOf, type PageRect } from '@shared/board'
import { type Material, materialHref, textIn } from '@shared/material'
import { clsx } from 'clsx'
import { BoxSelect, ListTree, MessageSquarePlus, Scissors, TextCursor, X } from 'lucide-react'
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { toast } from 'sonner'
import 'pdfjs-dist/web/pdf_viewer.css'
import { IconButton } from '@/components/Button'
import { useBoardCtx } from '@/board/context'
import { newClip } from '@/board/factory'
import { withMembership } from '@shared/whiteboard/membership'
import { clipLabel } from '@/session/describe'
import { useI18n } from '@/i18n/i18n'
import { loadMaterialText, pdfUrl, useMaterial } from './hooks'
import { loadPdf, pdfjs, type PdfDoc } from './pdf'
import { fractionRect, lineBoxes, linesAround } from './selection'
import { destPage } from './prepare'

// The reader (MATERIALS.md): a material open beside the board, drawn from its
// PDF. People do here what the AI does with its tools: select text or draw a
// box, then cut it onto the board as a clip, or point the AI at it. What has
// already been cut from a page is outlined on it.

type Mode = 'text' | 'region'
/**
 * Something picked on a page, waiting for what to do with it. Selected text
 * has `focus`, the boxes of the words; `rect` is then the lines around them.
 */
type Pick = { page: number; rect: PageRect; text: string; focus?: PageRect[] }

const PAGE_GAP = 16
/** How far outside the visible area pages are drawn (and beyond which they are let go). */
const KEEP_MARGIN = 1200

export function Reader({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()
  const { reader } = useBoardCtx()
  const target = useSyncExternalStore(reader.subscribe, reader.getSnapshot)
  const material = useMaterial(target?.materialId ?? '')
  if (!target) return null
  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-2">
      {material ? (
        <ReaderBody key={material.id} material={material} onClose={onClose} />
      ) : (
        <div className="flex h-full flex-col">
          <ReaderHeader title={t('material.missing')} onClose={onClose} />
        </div>
      )}
    </div>
  )
}

function ReaderHeader({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children?: React.ReactNode
}) {
  const { t } = useI18n()
  return (
    <div className="flex h-11 flex-none items-center gap-1 border-b border-line bg-(--chrome) px-2 backdrop-blur-xl">
      <span className="min-w-0 flex-1 truncate px-1 text-[13px] font-medium">{title}</span>
      {children}
      <IconButton label={t('reader.close')} onClick={onClose}>
        <X size={15} />
      </IconButton>
    </div>
  )
}

function ReaderBody({ material, onClose }: { material: Material; onClose: () => void }) {
  const { t } = useI18n()
  const ctx = useBoardCtx()
  const { reader, client, boardId, me, sessions, canvas, actions, engine } = ctx
  const target = useSyncExternalStore(reader.subscribe, reader.getSnapshot)
  const board = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const [doc, setDoc] = useState<PdfDoc | null>(null)
  const [failed, setFailed] = useState(false)
  const [mode, setMode] = useState<Mode>('text')
  const [pick, setPick] = useState<Pick | null>(null)
  const [current, setCurrent] = useState(1)
  const [showOutline, setShowOutline] = useState(false)
  const scroller = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const readOnly = client.readOnly

  useEffect(() => {
    let gone = false
    loadPdf(pdfUrl(boardId, material)).then(
      (d) => {
        if (!gone) setDoc(d)
      },
      () => {
        if (!gone) setFailed(true)
      },
    )
    return () => {
      gone = true
    }
  }, [boardId, material])

  useLayoutEffect(() => {
    const el = scroller.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => {
      if (e) setWidth(Math.max(200, Math.floor(e.contentRect.width) - 32))
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
    }
  }, [])

  /** Top of each page in the scroller, from the page sizes (pages are laid out before they are drawn). */
  const tops = useMemo(() => {
    const out: number[] = []
    let y = PAGE_GAP
    for (const p of material.pages) {
      out.push(y)
      y += Math.round((width * p.h) / p.w) + PAGE_GAP
    }
    return out
  }, [material.pages, width])

  // Go where the reader was asked to (a clip, a link), and point at the region.
  const lastTarget = useRef(0)
  useEffect(() => {
    const el = scroller.current
    if (!target || !el || !width || target.n === lastTarget.current) return
    lastTarget.current = target.n
    const page = Math.min(Math.max(1, target.page), material.pages.length)
    const p = material.pages[page - 1]
    const top = tops[page - 1] ?? 0
    const h = p ? (width * p.h) / p.w : 0
    const y = target.rect
      ? top + (target.rect.y + target.rect.h / 2) * h - el.clientHeight / 2
      : top - PAGE_GAP
    el.scrollTo({ top: Math.max(0, y) })
  }, [target, tops, width, material.pages])
  /** The region the reader was opened at, pointed out once. */
  const flash = target?.rect ? { page: target.page, rect: target.rect, n: target.n } : null

  const onScroll = () => {
    const el = scroller.current
    if (!el) return
    const mid = el.scrollTop + el.clientHeight * 0.35
    let n = 1
    for (let i = 0; i < tops.length; i++) if ((tops[i] ?? 0) <= mid) n = i + 1
    setCurrent(n)
  }

  const clips = useMemo(() => {
    const out = new Map<number, ItemOf<'clip'>[]>()
    for (const i of board.state.items.values())
      if (i.kind === 'clip' && i.data.materialId === material.id) {
        const list = out.get(i.data.page) ?? []
        list.push(i)
        out.set(i.data.page, list)
      }
    return out
  }, [board.state, material.id])

  const showOnBoard = useCallback(
    async (ids: string[]) => {
      await canvas.reveal()
      actions.current?.showItems(ids)
    },
    [canvas, actions],
  )

  const cut = (p: Pick) => {
    const center = engine.current?.viewportCenter() ?? { x: 0, y: 0 }
    const clip = newClip(client.view, me, material, p.page, p.rect, p.text, center, p.focus)
    client.apply(withMembership(client.view, [{ type: 'item.create', item: clip }]))
    setPick(null)
    window.getSelection()?.removeAllRanges()
    toast.success(t('reader.clipped'), {
      action: {
        label: t('reader.showClip'),
        onClick: () => {
          void showOnBoard([clip.id])
        },
      },
    })
  }

  const ask = (p: Pick) => {
    const label = p.text.trim()
      ? `${clipLabel({ page: p.page, text: p.text })}${p.text.trim().length > 32 ? '…' : ''}`
      : t('reader.regionLabel', { n: p.page })
    sessions.addToDraft({
      label: label.replace(/[[\]]/g, ''),
      href: materialHref({ materialId: material.id, page: p.page, rect: p.rect }),
    })
    setPick(null)
    window.getSelection()?.removeAllRanges()
  }

  // Text mode: what is selected in one page's text becomes the pick.
  const onPointerUp = () => {
    if (mode !== 'text') return
    requestAnimationFrame(() => {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || !sel.rangeCount) {
        setPick(null)
        return
      }
      const range = sel.getRangeAt(0)
      const start =
        range.startContainer instanceof Element
          ? range.startContainer
          : range.startContainer.parentElement
      const pageEl = start?.closest<HTMLElement>('[data-page]')
      if (!pageEl || !scroller.current?.contains(pageEl)) return
      const box = pageEl.getBoundingClientRect()
      const rects = [...range.getClientRects()].filter(
        (r) => r.width > 0 && r.height > 0 && r.bottom > box.top && r.top < box.bottom,
      )
      if (!rects.length) return
      const x0 = Math.max(box.left, Math.min(...rects.map((r) => r.left)))
      const y0 = Math.max(box.top, Math.min(...rects.map((r) => r.top)))
      const x1 = Math.min(box.right, Math.max(...rects.map((r) => r.right)))
      const y1 = Math.min(box.bottom, Math.max(...rects.map((r) => r.bottom)))
      const pad = 4
      const page = Number(pageEl.dataset.page)
      const focus = lineBoxes(rects).map((r) => fractionRect(r.left, r.top, r.right, r.bottom, box))
      const words = fractionRect(x0 - pad, y0 - pad, x1 + pad, y1 + pad, box)
      // Shown with the whole lines it is on, so a quote reads in its sentence.
      void loadMaterialText(boardId, material).then((text) => {
        setPick({
          page,
          rect: linesAround(text?.pages[page - 1] ?? [], focus) ?? words,
          text: sel.toString(),
          focus,
        })
      })
    })
  }

  const onRegion = async (page: number, rect: PageRect) => {
    const text = await loadMaterialText(boardId, material)
    setPick({ page, rect, text: textIn(text?.pages[page - 1] ?? [], rect) })
  }

  const jump = (page: number) => {
    const el = scroller.current
    if (el) el.scrollTo({ top: Math.max(0, (tops[page - 1] ?? 0) - PAGE_GAP) })
    setShowOutline(false)
  }

  return (
    <>
      <ReaderHeader title={material.name} onClose={onClose}>
        <span className="px-1.5 text-[12px] text-muted tabular-nums">
          {t('reader.pageOf', { n: current, total: material.pages.length })}
        </span>
        {material.outline.length > 0 && (
          <IconButton
            label={t('reader.outline')}
            aria-pressed={showOutline}
            onClick={() => {
              setShowOutline((v) => !v)
            }}
          >
            <ListTree size={15} />
          </IconButton>
        )}
        {!readOnly && (
          <div
            role="radiogroup"
            aria-label={t('reader.mode')}
            className="mx-1 inline-flex items-center rounded-lg border border-line bg-surface-2 p-0.5"
          >
            {(
              [
                ['text', TextCursor, t('reader.modeText')],
                ['region', BoxSelect, t('reader.modeRegion')],
              ] as const
            ).map(([value, Icon, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                title={label}
                aria-label={label}
                onClick={() => {
                  setMode(value)
                  setPick(null)
                }}
                className={clsx(
                  'grid size-7 place-items-center rounded-md transition-colors',
                  mode === value ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink',
                )}
              >
                <Icon size={14} />
              </button>
            ))}
          </div>
        )}
      </ReaderHeader>
      <div className="relative min-h-0 flex-1">
        {showOutline && (
          <nav
            aria-label={t('reader.outline')}
            className="absolute inset-y-0 left-0 z-20 w-[min(320px,85%)] overflow-y-auto border-r border-line bg-surface py-2 shadow-lg"
          >
            {material.outline.map((o, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  jump(o.page)
                }}
                className="flex w-full items-baseline gap-2 py-1 pr-3 text-left text-[13px] hover:bg-surface-2"
                style={{ paddingLeft: 12 + o.level * 14 }}
              >
                <span className="min-w-0 flex-1">{o.title}</span>
                <span className="flex-none text-[11.5px] text-muted tabular-nums">{o.page}</span>
              </button>
            ))}
          </nav>
        )}
        <div
          ref={scroller}
          onScroll={onScroll}
          onPointerUp={onPointerUp}
          className="h-full overflow-y-auto"
        >
          {failed && <p className="p-6 text-center text-[13px] text-muted">{t('reader.failed')}</p>}
          {doc && width > 0 && (
            <div className="relative mx-auto" style={{ width }}>
              {material.pages.map((p, i) => (
                <ReaderPage
                  key={i}
                  doc={doc}
                  n={i + 1}
                  width={width}
                  height={Math.round((width * p.h) / p.w)}
                  top={tops[i] ?? 0}
                  scroller={scroller}
                  mode={readOnly ? 'text' : mode}
                  clips={clips.get(i + 1) ?? []}
                  flash={flash?.page === i + 1 ? flash : null}
                  pick={pick?.page === i + 1 ? pick : null}
                  onRegion={(r) => void onRegion(i + 1, r)}
                  onShowClip={(id) => void showOnBoard([id])}
                  onJump={jump}
                  actions={
                    pick?.page === i + 1 && !readOnly ? (
                      <PickBar
                        onCut={() => {
                          cut(pick)
                        }}
                        onAsk={() => {
                          ask(pick)
                        }}
                        onCancel={() => {
                          setPick(null)
                          window.getSelection()?.removeAllRanges()
                        }}
                      />
                    ) : null
                  }
                />
              ))}
              <div style={{ height: (tops.at(-1) ?? 0) + PAGE_GAP }} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function PickBar({
  onCut,
  onAsk,
  onCancel,
}: {
  onCut: () => void
  onAsk: () => void
  onCancel: () => void
}) {
  const { t } = useI18n()
  const button =
    'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-medium hover:bg-surface-2'
  return (
    <div
      className="flex items-center gap-0.5 rounded-lg border border-line bg-surface p-0.5 shadow-lg"
      onPointerUp={(e) => {
        e.stopPropagation()
      }}
    >
      <button type="button" className={button} onClick={onCut}>
        <Scissors size={14} />
        {t('reader.cut')}
      </button>
      <button type="button" className={button} onClick={onAsk}>
        <MessageSquarePlus size={14} />
        {t('reader.ask')}
      </button>
      <IconButton label={t('reader.cancel')} onClick={onCancel}>
        <X size={14} />
      </IconButton>
    </div>
  )
}

function ReaderPage({
  doc,
  n,
  width,
  height,
  top,
  scroller,
  mode,
  clips,
  flash,
  pick,
  onRegion,
  onShowClip,
  onJump,
  actions,
}: {
  doc: PdfDoc
  n: number
  width: number
  height: number
  top: number
  scroller: React.RefObject<HTMLDivElement | null>
  mode: Mode
  clips: ItemOf<'clip'>[]
  flash: { rect: PageRect; n: number } | null
  pick: Pick | null
  onRegion: (r: PageRect) => void
  onShowClip: (id: string) => void
  /** Go to a page of this material (a link inside it). */
  onJump: (page: number) => void
  actions: React.ReactNode
}) {
  const { t } = useI18n()
  const near = useNearView(scroller, top, height)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null)
  const links = usePageLinks(doc, n, near)

  useEffect(() => {
    const canvas = canvasRef.current
    const layer = textRef.current
    if (!near || !canvas || !layer) return
    let gone = false
    let cancel: (() => void) | null = null
    void Promise.all([doc.getPage(n), pdfjs()]).then(([page, m]) => {
      if (gone) return
      const base = page.getViewport({ scale: 1 })
      const scale = width / base.width
      const ratio = window.devicePixelRatio || 1
      const vp = page.getViewport({ scale: scale * ratio })
      canvas.width = Math.round(vp.width)
      canvas.height = Math.round(vp.height)
      const task = page.render({ canvas, viewport: vp })
      task.promise.catch(() => undefined)
      layer.replaceChildren()
      layer.style.setProperty('--scale-factor', String(scale))
      const text = new m.TextLayer({
        textContentSource: page.streamTextContent(),
        container: layer,
        viewport: page.getViewport({ scale }),
      })
      text.render().catch(() => undefined)
      cancel = () => {
        task.cancel()
        text.cancel()
      }
    })
    return () => {
      gone = true
      cancel?.()
    }
  }, [near, doc, n, width])

  const local = (e: ReactPointerEvent) => {
    const box = e.currentTarget.getBoundingClientRect()
    return { x: e.clientX - box.left, y: e.clientY - box.top }
  }

  const sel = drag && {
    x: Math.min(drag.x0, drag.x1),
    y: Math.min(drag.y0, drag.y1),
    w: Math.abs(drag.x1 - drag.x0),
    h: Math.abs(drag.y1 - drag.y0),
  }

  return (
    <div
      data-page={n}
      className="absolute left-0 bg-white shadow-sm"
      style={{ top, width, height }}
    >
      {near && <canvas ref={canvasRef} aria-hidden className="absolute inset-0 size-full" />}
      {near && (
        <div
          ref={textRef}
          className={clsx('textLayer', mode === 'region' && 'pointer-events-none')}
        />
      )}
      {mode === 'text' &&
        links.map((l, i) => (
          <button
            key={i}
            type="button"
            title={l.url ?? t('reader.toPage', { n: l.page ?? 0 })}
            aria-label={l.url ?? t('reader.toPage', { n: l.page ?? 0 })}
            onClick={() => {
              if (l.url) window.open(l.url, '_blank', 'noopener,noreferrer')
              else if (l.page) onJump(l.page)
            }}
            className="absolute cursor-pointer rounded-[2px] hover:bg-(--focus)/10"
            style={box(l.rect, width, height)}
          />
        ))}
      {clips.map((c) => (
        <div
          key={c.id}
          className="pointer-events-none absolute rounded-[2px] border border-dashed border-(--focus)/60 bg-(--focus)/5"
          style={box(c.data.rect, width, height)}
        >
          <button
            type="button"
            title={t('reader.showClip')}
            onClick={() => {
              onShowClip(c.id)
            }}
            className="pointer-events-auto absolute -top-2.5 right-1 rounded-sm border border-line bg-surface px-1 text-[10.5px] leading-4 text-muted shadow-sm hover:text-ink"
          >
            {t('reader.onBoard')}
          </button>
        </div>
      ))}
      {flash && (
        <div
          key={flash.n}
          className="pointer-events-none absolute animate-[reader-flash_2.4s_ease-out_forwards] rounded-[2px] ring-2 ring-(--focus)"
          style={box(flash.rect, width, height)}
        />
      )}
      {pick && (
        <div
          className="pointer-events-none absolute rounded-[2px] bg-(--focus)/15 ring-1 ring-(--focus)"
          style={box(pick.rect, width, height)}
        />
      )}
      {mode === 'region' && (
        <div
          className="absolute inset-0 cursor-crosshair touch-none"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId)
            const p = local(e)
            setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y })
          }}
          onPointerMove={(e) => {
            if (!drag) return
            const p = local(e)
            setDrag({ ...drag, x1: p.x, y1: p.y })
          }}
          onPointerUp={(e) => {
            if (!sel) return
            e.stopPropagation()
            setDrag(null)
            if (sel.w < 6 || sel.h < 6) return
            onRegion({
              x: Math.max(0, sel.x / width),
              y: Math.max(0, sel.y / height),
              w: Math.min(1 - sel.x / width, sel.w / width),
              h: Math.min(1 - sel.y / height, sel.h / height),
            })
          }}
        >
          {sel && (
            <div
              className="absolute rounded-[2px] bg-(--focus)/10 ring-1 ring-(--focus)"
              style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h }}
            />
          )}
        </div>
      )}
      {pick && actions && (
        <div
          className="absolute z-10"
          style={{
            left: Math.min(Math.max(0, pick.rect.x * width), Math.max(0, width - 260)),
            top: Math.min((pick.rect.y + pick.rect.h) * height + 6, height - 40),
          }}
        >
          {actions}
        </div>
      )}
    </div>
  )
}

type PageLink = { rect: PageRect; url?: string; page?: number }

/** The links on a page of the PDF: to its other pages ("see 3.2", "[11]") or to the web. */
function usePageLinks(doc: PdfDoc, n: number, near: boolean): PageLink[] {
  const [links, setLinks] = useState<PageLink[]>([])
  useEffect(() => {
    if (!near) return
    let gone = false
    void doc.getPage(n).then(async (page) => {
      const vp = page.getViewport({ scale: 1 })
      const out: PageLink[] = []
      const annots = (await page.getAnnotations({ intent: 'display' })) as {
        subtype?: string
        rect?: number[]
        url?: string
        dest?: string | unknown[] | null
      }[]
      for (const a of annots) {
        if (a.subtype !== 'Link' || !a.rect) continue
        // PDF space to the page as drawn (y down), through the viewport's transform.
        const [ta = 1, tb = 0, tc = 0, td = 1, te = 0, tf = 0] = vp.transform
        const at = (px = 0, py = 0) => [ta * px + tc * py + te, tb * px + td * py + tf] as const
        const [x0, y0] = at(a.rect[0], a.rect[1])
        const [x1, y1] = at(a.rect[2], a.rect[3])
        const rect = {
          x: Math.min(x0, x1) / vp.width,
          y: Math.min(y0, y1) / vp.height,
          w: Math.abs(x1 - x0) / vp.width,
          h: Math.abs(y1 - y0) / vp.height,
        }
        if (a.url && /^https?:/i.test(a.url)) out.push({ rect, url: a.url })
        else if (a.dest) {
          const to = await destPage(doc, a.dest as Parameters<typeof destPage>[1])
          if (to) out.push({ rect, page: to })
        }
      }
      if (!gone) setLinks(out)
    })
    return () => {
      gone = true
    }
  }, [doc, n, near])
  return links
}

function box(r: PageRect, width: number, height: number) {
  return { left: r.x * width, top: r.y * height, width: r.w * width, height: r.h * height }
}

/** Whether a page at `top`..`top + height` in the scroller is within reach of the view. */
function useNearView(
  scroller: React.RefObject<HTMLDivElement | null>,
  top: number,
  height: number,
): boolean {
  const [near, setNear] = useState(false)
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    const check = () => {
      const view0 = el.scrollTop - KEEP_MARGIN
      const view1 = el.scrollTop + el.clientHeight + KEEP_MARGIN
      setNear(top + height > view0 && top < view1)
    }
    check()
    el.addEventListener('scroll', check, { passive: true })
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', check)
      ro.disconnect()
    }
  }, [scroller, top, height])
  return near
}
